import React from 'react';
import { Bookmark, Plus, User, Globe, Clock, Edit2, Trash2 } from 'lucide-react';
import type { LibraryComponent } from '../../schemas/validation';
import { getComponentIcon, getComponentColorScheme } from './componentOptions';

interface LibraryTabProps {
  isLoading: boolean;
  components: LibraryComponent[];
  searchTerm: string;
  selectedType: string;
  currentUserId: string | null;
  onSelectComponent: (id: string) => void;
  onEditComponent?: (component: LibraryComponent) => void;
  onDeleteComponent: (component: LibraryComponent) => void;
  isDeleting: boolean;
  isAddingToPage: boolean;
}

export const LibraryTab: React.FC<LibraryTabProps> = ({
  isLoading,
  components,
  searchTerm,
  selectedType,
  currentUserId,
  onSelectComponent,
  onEditComponent,
  onDeleteComponent,
  isDeleting,
  isAddingToPage,
}) => {
  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading library components...</p>
      </div>
    );
  }

  if (components.length === 0) {
    return (
      <div className="text-center py-12">
        <Bookmark size={48} className="mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {searchTerm || selectedType !== 'all' ? 'No components found' : 'No saved components'}
        </h3>
        <p className="text-gray-500">
          {searchTerm || selectedType !== 'all'
            ? 'Try adjusting your search terms or filters'
            : 'Save components to your library to reuse them across pages'}
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {components.map(component => {
        const IconComponent = getComponentIcon(component.type);
        const colors = getComponentColorScheme(component.type);
        const isOwner = component.userId === currentUserId;

        return (
          <div
            key={component.id}
            className={`group relative ${colors.bg} border border-gray-200 rounded-lg p-4 ${colors.hoverBorder} ${colors.hoverBg} transition-all duration-200`}
          >
            <div className="flex items-start space-x-3">
              {/* Component Icon */}
              <div
                className={`flex items-center justify-center w-10 h-10 ${colors.iconBg} rounded-lg flex-shrink-0 transition-colors`}
              >
                <IconComponent size={20} className={`${colors.iconColor} ${colors.hoverIcon}`} />
              </div>

              {/* Component Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h4 className={`text-sm font-medium text-gray-900 truncate ${colors.hoverText}`}>
                    {component.name}
                  </h4>
                  <div className="flex items-center space-x-1 ml-2">
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${colors.iconBg} ${colors.iconColor} capitalize`}
                    >
                      {component.type}
                    </span>
                    {component.isPublic && (
                      <div title="Public component">
                        <Globe size={12} className="text-green-500" />
                      </div>
                    )}
                  </div>
                </div>

                {component.description && (
                  <p
                    className={`text-xs text-gray-500 leading-relaxed mb-2 ${colors.hoverDescription}`}
                  >
                    {component.description}
                  </p>
                )}

                {component.tags && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {component.tags
                      .split(',')
                      .slice(0, 3)
                      .map((tag, index) => (
                        <span
                          key={index}
                          className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded"
                        >
                          {tag.trim()}
                        </span>
                      ))}
                    {component.tags.split(',').length > 3 && (
                      <span className="text-xs text-gray-400">
                        +{component.tags.split(',').length - 3} more
                      </span>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between text-xs text-gray-400">
                  <div className="flex items-center space-x-1">
                    <User size={12} />
                    <span>{isOwner ? 'You' : 'Shared'}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Clock size={12} />
                    <span>{new Date(component.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-2 mt-3 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => onSelectComponent(component.id)}
                    disabled={isAddingToPage}
                    className="flex-1 flex items-center justify-center space-x-1 px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus size={14} />
                    <span>Add to Page</span>
                  </button>
                  {isOwner && onEditComponent && (
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        onEditComponent(component);
                      }}
                      disabled={isAddingToPage}
                      className="flex items-center justify-center space-x-1 px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Edit library component"
                    >
                      <Edit2 size={14} />
                      <span>Edit</span>
                    </button>
                  )}
                  {isOwner && (
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        onDeleteComponent(component);
                      }}
                      disabled={isAddingToPage || isDeleting}
                      className="flex items-center justify-center p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete from library"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
